import { HCDatepicker } from '@chiroup/components/datepicker/HCDatepicker';
import {
  DateValue,
  GroupValue,
  SurveyOption,
  SurveyQuestion,
  SurveyQuestionAnswerNonGroup,
} from '@chiroup/core/types/Survey.type';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { renderSurveyHtml } from './htmlRenderer';
import {
  SoftCheckboxMarker,
  SoftOptionCard,
  SoftPillButton,
  SoftQuestionShell,
  SoftRadioMarker,
  SoftTextArea,
  SoftTextField,
} from './primitives';
import { SoftQuestionProps, surveyVerbiage } from './types';

// Soft-styled renderer for `type: 'group'` survey questions. The legacy
// `SurveyQuestionGroup` rendered each sub-question with the old
// `SurveyQuestionContent` dispatcher — staying outside the Soft Card
// system. This component re-skins the group itself: one outer
// `SoftQuestionShell` with the group title, then per-sub-question
// title + inline body using the same Soft primitives the standalone
// question types use, capped by a single shared "Continue" button.

const groupVerbiage = {
  en: { allRequired: 'Please answer all required questions to continue.' },
  es: { allRequired: 'Responda todas las preguntas obligatorias para continuar.' },
};

// A required sub-question is answered when it has an answer — but a required
// multi-select must have at least one selection (empty selection ≠ answered).
const isSubAnswered = (answer?: SurveyQuestionAnswerNonGroup): boolean => {
  if (!answer) return false;
  if (answer.type === 'checkbox') {
    return Array.isArray(answer.value) && answer.value.length > 0;
  }
  if (answer.type === 'input' || answer.type === 'textarea') {
    return typeof answer.value === 'string' && answer.value.trim().length > 0;
  }

  return true;
};

const SubQuestionTitle: React.FC<{ html: string; required?: boolean }> = ({ html, required }) => (
  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
    {renderSurveyHtml(html)}
    {required ? <span className="ml-1 text-red-500">*</span> : null}
  </h3>
);

type SubBodyProps = {
  subQuestion: SurveyQuestion;
  value: SurveyQuestionAnswerNonGroup | undefined;
  onChange: (val: SurveyQuestionAnswerNonGroup | undefined) => void;
  primaryColor: string;
  disabled: boolean;
  language: 'en' | 'es';
};

// -- Radio sub-body -------------------------------------------------------

const SubRadioBody: React.FC<SubBodyProps> = ({
  subQuestion,
  value,
  onChange,
  primaryColor,
  disabled,
}) => {
  const selectedId = value?.type === 'radio' ? String(value.value) : null;

  return (
    <ul role="list" className="flex flex-col gap-2.5">
      {(subQuestion.options || []).map((opt: SurveyOption, i: number) => {
        const id = String(opt?.id ?? i);

        return (
          <li key={id}>
            <SoftOptionCard
              primaryColor={primaryColor}
              disabled={disabled}
              onClick={() => onChange({ type: 'radio', value: id })}
              testCy={opt.display}
            >
              <SoftRadioMarker primaryColor={primaryColor} selected={selectedId === id} />
              <span className="flex-1 text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100">
                {opt.display}
              </span>
            </SoftOptionCard>
          </li>
        );
      })}
    </ul>
  );
};

// -- Checkbox sub-body ----------------------------------------------------

const SubCheckboxBody: React.FC<SubBodyProps> = ({
  subQuestion,
  value,
  onChange,
  primaryColor,
  disabled,
}) => {
  const current: string[] =
    value?.type === 'checkbox' ? ((value.value as unknown as string[]) ?? []).map(String) : [];
  const isChecked = (id: string) => current.includes(id);

  const toggle = (id: string) => {
    const next = isChecked(id) ? current.filter(v => v !== id) : [...current, id];

    if (next.length === 0) {
      onChange(undefined);

      return;
    }
    onChange({ type: 'checkbox', value: next });
  };

  return (
    <ul role="list" className="flex flex-col gap-2.5">
      {(subQuestion.options || []).map((opt: SurveyOption, i: number) => {
        const id = String(opt?.id ?? i);
        const checked = isChecked(id);

        return (
          <li key={id}>
            <SoftOptionCard
              primaryColor={primaryColor}
              disabled={disabled}
              onClick={() => toggle(id)}
              alignTop
              testCy={opt.display}
            >
              <SoftCheckboxMarker primaryColor={primaryColor} checked={checked} />
              <span className="flex-1 text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100">
                {opt.display}
              </span>
            </SoftOptionCard>
          </li>
        );
      })}
    </ul>
  );
};

// -- Input sub-body -------------------------------------------------------

const SubInputBody: React.FC<SubBodyProps> = ({
  subQuestion,
  value,
  onChange,
  primaryColor,
  disabled,
}) => {
  const v = value?.type === 'input' ? (value.value as string) : '';
  const inputType = (subQuestion.typeOptions?.type as string) || 'text';

  return (
    <SoftTextField
      primaryColor={primaryColor}
      type={inputType}
      name={`question-${subQuestion.id}`}
      value={v}
      onChange={e => {
        const next = e.target.value;
        if (!next) onChange(undefined);
        else onChange({ type: 'input', value: next });
      }}
      disabled={disabled}
    />
  );
};

// -- Textarea sub-body ----------------------------------------------------

const SubTextareaBody: React.FC<SubBodyProps> = ({
  subQuestion,
  value,
  onChange,
  primaryColor,
  disabled,
}) => {
  const v = value?.type === 'textarea' ? (value.value as string) : '';

  return (
    <SoftTextArea
      primaryColor={primaryColor}
      name={`question-${subQuestion.id}`}
      value={v}
      onChange={e => {
        const next = e.target.value;
        if (!next) onChange(undefined);
        else onChange({ type: 'textarea', value: next });
      }}
      disabled={disabled}
      rows={4}
      placeholder={subQuestion.typeOptions?.placeHolder}
    />
  );
};

// -- Slider sub-body ------------------------------------------------------

const SubSliderBody: React.FC<SubBodyProps> = ({
  subQuestion,
  value,
  onChange,
  primaryColor,
  disabled,
}) => {
  const opts = (subQuestion.typeOptions || {}) as {
    min?: number;
    max?: number;
    left?: string;
    right?: string;
    start?: number;
    step?: number;
    notLessThan?: number;
    notMoreThan?: number;
  };
  const min = opts.min ?? 0;
  const max = opts.max ?? 10;
  const step = opts.step || 1;
  const initial =
    value?.type === 'slider' && typeof value.value === 'number'
      ? value.value
      : typeof opts.notLessThan === 'number'
        ? opts.notLessThan
        : typeof opts.notMoreThan === 'number'
          ? opts.notMoreThan
          : typeof opts.start === 'number'
            ? opts.start
            : min;
  const clamped = Math.max(min, Math.min(max, initial));

  // Sub-bodies need to seed the parent group's value on first render so
  // that "Continue" can submit a meaningful answer even when the user
  // accepted the default and never moved the thumb. The legacy slider
  // also reported its initial value on mount.
  useEffect(() => {
    if (value?.type !== 'slider') {
      onChange({ type: 'slider', value: clamped });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once
  }, []);

  const sliderClass = useMemo(
    () => `range-slider__range-${primaryColor.replace('#', '')}`,
    [primaryColor]
  );

  // Duplicated from SoftSliderQuestion intentionally — the stylesheet is
  // keyed by color, so multiple instances with the same color share one
  // <style> element; this keeps the standalone slider and the group
  // sub-slider visually identical without coupling their lifecycles.
  useEffect(() => {
    const id = `slider-styles-${sliderClass}`;
    const existing = document.getElementById(id);

    if (existing) existing.remove();
    const sheet = document.createElement('style');
    sheet.setAttribute('id', id);
    sheet.textContent = `
      .${sliderClass}::-webkit-slider-thumb { appearance: none; width: 22px; height: 22px; border-radius: 50%; background: ${primaryColor}; cursor: pointer; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
      .${sliderClass}::-moz-range-thumb { width: 22px; height: 22px; border: 0; border-radius: 50%; background: ${primaryColor}; cursor: pointer; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
      .${sliderClass}:focus { outline: none; }
      .${sliderClass}:focus::-webkit-slider-thumb { box-shadow: 0 0 0 3px white, 0 0 0 6px ${primaryColor}; }
    `;
    document.head.appendChild(sheet);

    return () => sheet.remove();
  }, [sliderClass, primaryColor]);

  const onChangeRange = (event: ChangeEvent<HTMLInputElement>) => {
    let next = Number(event.target.value);

    if (typeof opts.notLessThan === 'number' && next < opts.notLessThan) next = opts.notLessThan;
    if (typeof opts.notMoreThan === 'number' && next > opts.notMoreThan) next = opts.notMoreThan;
    onChange({ type: 'slider', value: next });
  };

  const fillPct = max === min ? 0 : ((clamped - min) / (max - min)) * 100;

  return (
    <div className="portal-card p-5 flex flex-col gap-4">
      <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        <span>{opts.left ?? min}</span>
        <span>{opts.right ?? max}</span>
      </div>
      <input
        type="range"
        className={`${sliderClass} w-full appearance-none h-2 rounded-full`}
        value={clamped}
        min={min}
        max={max}
        step={step}
        onChange={onChangeRange}
        disabled={disabled}
        style={{
          background: `linear-gradient(to right, ${primaryColor} 0%, ${primaryColor} ${fillPct}%, rgba(0,0,0,0.06) ${fillPct}%, rgba(0,0,0,0.06) 100%)`,
        }}
      />
      <p
        className="self-center inline-flex items-center justify-center min-w-12 px-3 py-1 rounded-full text-base font-semibold tabular-nums text-white"
        style={{ backgroundColor: primaryColor }}
      >
        {clamped}
      </p>
    </div>
  );
};

// -- Date sub-body --------------------------------------------------------

const SubDateBody: React.FC<SubBodyProps> = ({
  subQuestion,
  value,
  onChange,
  primaryColor,
  disabled,
  language,
}) => (
  <div className="portal-card p-5 flex justify-center" aria-disabled={disabled || undefined}>
    <HCDatepicker
      options={subQuestion.typeOptions}
      initialValue={value?.type === 'date' ? (value.value as DateValue) : undefined}
      onChange={(e?: DateValue) => {
        if (!e) onChange(undefined);
        else onChange({ type: 'date', value: e });
      }}
      primaryColor={primaryColor}
      language={language}
      size="normal"
    />
  </div>
);

// -- Group component ------------------------------------------------------

export const SoftGroupQuestion: React.FC<SoftQuestionProps> = props => {
  const {
    question,
    progress,
    language,
    primaryColor,
    disabled,
    hidden,
    showPrevious,
    onBack,
    onSelect,
  } = props;
  const v = surveyVerbiage[language];
  const subQuestions = question.typeOptions?.subQuestions || [];
  const [val, setVal] = useState<GroupValue>({});
  const [formError, setFormError] = useState<string>('');

  const setSub = (id: string, ans: SurveyQuestionAnswerNonGroup | undefined) => {
    setFormError('');
    setVal(prev => {
      if (ans === undefined) {
        const { [id]: _drop, ...rest } = prev;

        return rest;
      }

      return { ...prev, [id]: ans };
    });
  };

  const handleSubmit = async () => {
    setFormError('');

    // Every sub-question marked `forceAnswer` must be answered — including
    // multi-selects, which now support a required setting (a required
    // multi-select needs at least one selection).
    const required = subQuestions.filter(q => q.typeOptions?.forceAnswer);

    if (required.length && !required.every(q => isSubAnswered(val?.[q.id]))) {
      setFormError(groupVerbiage[language].allRequired);

      return;
    }

    await onSelect({ type: 'group', value: val });
    setVal({});
  };

  const renderSubBody = (sq: SurveyQuestion) => {
    const sharedProps: SubBodyProps = {
      subQuestion: sq,
      value: val[sq.id],
      onChange: ans => setSub(sq.id, ans),
      primaryColor,
      disabled,
      language,
    };

    switch (sq.type) {
      case 'radio':
        return <SubRadioBody {...sharedProps} />;
      case 'checkbox':
        return <SubCheckboxBody {...sharedProps} />;
      case 'input':
        return <SubInputBody {...sharedProps} />;
      case 'textarea':
        return <SubTextareaBody {...sharedProps} />;
      case 'slider':
        return <SubSliderBody {...sharedProps} />;
      case 'date':
        return <SubDateBody {...sharedProps} />;
      default:
        // Group-nested-in-group, bodypicker, picture, video — not
        // supported in the Soft group renderer. Surface the gap rather
        // than silently dropping the sub-question.
        return (
          <p className="text-sm text-red-500">
            Unsupported question type in group: <code>{sq.type}</code>.
          </p>
        );
    }
  };

  return (
    <SoftQuestionShell
      question={question}
      progress={progress}
      language={language}
      primaryColor={primaryColor}
      hidden={hidden}
      showPrevious={showPrevious}
      onBack={onBack}
    >
      <div className="flex flex-col gap-7">
        {subQuestions.map(sq => (
          <div key={sq.id} className="flex flex-col gap-3">
            <SubQuestionTitle html={sq.question} required={sq.typeOptions?.forceAnswer} />
            {renderSubBody(sq)}
          </div>
        ))}
      </div>
      {formError ? <p className="text-sm text-red-500">{formError}</p> : null}
      <SoftPillButton primaryColor={primaryColor} disabled={disabled} onClick={handleSubmit}>
        {v.next}
      </SoftPillButton>
    </SoftQuestionShell>
  );
};
